import { nowIsoAndUnix } from "../date-time/iso-and-unix";
import type {
    Percentage,
    PercentageKey} from "../percentage";
import {
    percentageZodSchema,
    roundPercentage,
    toPercentageFromPercentageKey,
    toPercentageKey,
} from "../percentage";
import type {
    AmountsAndVatRate,
    AmountsByVatRate,
    Price,
    PriceTerm} from "./price";
import {
    priceFactory,
    sortAmountsByVatRate,
    termFromPrice,
} from "./price";
import { priceIdFactory } from "./price-id";

const subtractFromPrice = (price: Price, pricesToSubtract: Price[]) => {
    if (pricesToSubtract.length === 0) return price;
    const _price = price.vatAmounts
        ? price
        : priceFactory({
              vatAmounts: {
                  [toPercentageKey(price.vat)]: {
                      vatAmount: price.vatAmount,
                      amountIncludingVat: price.amountIncludingVat,
                  },
              },
          });

    const difference = pricesToSubtract.reduce((diff, price) => {
        // Make sure they are all the same currency
        if (diff.currencyCode !== price.currencyCode)
            throw new Error(
                "Subtraction of prices of different currencies not yet supported",
            );

        // Subtract the raw amounts
        diff.amountExcludingVat -= price.amountExcludingVat;
        diff.amountIncludingVat -= price.amountIncludingVat;

        // Calculate the full vat amount
        diff.vatAmount -= price.vatAmount;

        // Vat amounts sorted by vat percentage
        const vatAmountsSorted: AmountsByVatRate = {
            ...Object.entries(sortAmountsByVatRate(price)).reduce(
                (acc, [key, data]) => {
                    const percentageKey: PercentageKey = key;
                    const amountsForVatRate: AmountsAndVatRate = {
                        amountIncludingVat:
                            (acc[percentageKey]?.amountIncludingVat || 0) -
                            data.amountIncludingVat,
                        amountExcludingVat:
                            (acc[percentageKey]?.amountExcludingVat || 0) -
                            data.amountExcludingVat,
                        vatAmount:
                            (acc[percentageKey]?.vatAmount || 0) -
                            data.vatAmount,
                        vatRate: toPercentageFromPercentageKey(percentageKey),
                    };
                    if (amountsForVatRate.vatAmount !== 0)
                        acc[percentageKey] = amountsForVatRate;
                    if (amountsForVatRate.vatAmount === 0)
                        delete acc[percentageKey];
                    return acc;
                },
                { ...(diff.vatAmounts || {}) } as AmountsByVatRate,
            ),
        };

        diff.vatAmounts = vatAmountsSorted;

        return diff;
    }, termFromPrice(_price));

    const vatRateCalc = (diff: PriceTerm): Percentage => {
        if (diff.amountExcludingVat === 0) return 0 as Percentage;
        if (diff.vatAmounts && Object.keys(diff.vatAmounts).length === 1)
            return toPercentageFromPercentageKey(
                Object.keys(diff.vatAmounts)[0],
            );

        return percentageZodSchema.parse(
            diff.vatAmount / diff.amountExcludingVat,
        );
    };
    const vatRate = vatRateCalc(difference);

    const result: Price = {
        _type: "PRICE",
        _createdAt: nowIsoAndUnix(),
        _id: priceIdFactory(),
        amountExcludingVat: Math.round(difference.amountExcludingVat),
        amountIncludingVat: Math.round(difference.amountIncludingVat),
        currencyCode: difference.currencyCode,
        vatAmounts: difference.vatAmounts,
        vat: vatRate,
        vatRounded: roundPercentage(vatRate),
        vatAmount: difference.vatAmount,
        tags: [],
    };

    return result;
};

export { subtractFromPrice };
